import { useState, useRef, useCallback, useEffect } from "react";

function useMarkers(
  mapRef,
  onMarkerClick = null,
  onMarkerRightClick = null,
  onMarkerDragEnd = null
) {
  const [coordinates, setCoordinates] = useState([]);

  const markersRef = useRef([]);
  const polylineRef = useRef(null);
  const progressPolylineRef = useRef(null);
  const progressIndexRef = useRef(-1);
  const shadowPolylineRef = useRef(null); // 그림자 폴리라인 참조
  const shadowPolylineRefs = useRef([]); // 다중 그림자 폴리라인 참조 배열
  const distanceLabelsRef = useRef([]);

  const onMarkerClickRef = useRef(onMarkerClick);
  const onMarkerRightClickRef = useRef(onMarkerRightClick);
  const onMarkerDragEndRef = useRef(onMarkerDragEnd);

  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  useEffect(() => {
    onMarkerRightClickRef.current = onMarkerRightClick;
  }, [onMarkerRightClick]);

  useEffect(() => {
    onMarkerDragEndRef.current = onMarkerDragEnd;
  }, [onMarkerDragEnd]);

  const shadowConfig = {
    offset: 0.0001, // 기존 단일 오프셋(기본값 유지)
    offsetLat: -0.00001, // 그림자 위도 오프셋
    offsetLng: 0.00001, // 그림자 경도 오프셋
    opacity: 0.3, // 그림자 투명도
    color: "#000000", // 그림자 색상
    strokeWeight: 7, // 그림자 두께
    enableMultipleShadows: false, // 다중 그림자 효과 활성화
    shadowLayers: 2, // 그림자 레이어 수 (enableMultipleShadows가 true일 때)
  };

  const calculate3DDistance = useCallback((coord1, coord2) => {
    const R = 6371e3;
    const lat1Rad = (coord1.lat * Math.PI) / 180;
    const lat2Rad = (coord2.lat * Math.PI) / 180;
    const deltaLat = ((coord2.lat - coord1.lat) * Math.PI) / 180;
    const deltaLng = ((coord2.lng - coord1.lng) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const flatDistance = R * c;
    const vertical = Math.abs(coord1.alt - coord2.alt);
    return Math.sqrt(flatDistance ** 2 + vertical ** 2);
  }, []);

  const updatePolyline = useCallback(() => {
    const path = markersRef.current.map((marker) => marker.position);
    const map = mapRef.current;
    const progressIndex = progressIndexRef.current;

    const routeDashSymbol = {
      path: "M 0,-2 0,2",
      strokeColor: "#37D5A4",
      strokeOpacity: 1,
      strokeWeight: 3,
      scale: 1,
    };

    if (path.length >= 2) {
      if (shadowPolylineRefs.current.length > 0) {
        shadowPolylineRefs.current.forEach((shadow) => {
          if (shadow) shadow.setMap(null);
        });
        shadowPolylineRefs.current = [];
      }

      if (shadowConfig.enableMultipleShadows) {
        for (let i = 0; i < shadowConfig.shadowLayers; i++) {
          const baseOffsetLat = shadowConfig.offsetLat ?? shadowConfig.offset ?? 0;
          const baseOffsetLng = shadowConfig.offsetLng ?? shadowConfig.offset ?? 0;
          const offsetLat = baseOffsetLat * (i + 1);
          const offsetLng = baseOffsetLng * (i + 1);
          const opacity = shadowConfig.opacity / (i + 1);

          const shadowPath = path.map((point) => ({
            lat: point.lat + offsetLat,
            lng: point.lng + offsetLng,
          }));

          const shadowPolyline = new window.google.maps.Polyline({
            map,
            path: shadowPath,
            strokeWeight: 0,
            strokeColor: shadowConfig.color,
            strokeOpacity: 0,
            clickable: false,
            zIndex: 1 + i,
            icons: [
              {
                icon: {
                  ...routeDashSymbol,
                  strokeColor: shadowConfig.color,
                  strokeOpacity: opacity,
                  strokeWeight: shadowConfig.strokeWeight,
                },
                offset: "0",
                repeat: "14px",
              },
            ],
          });

          shadowPolylineRefs.current.push(shadowPolyline);
        }
      } else {
        const offsetLat = shadowConfig.offsetLat ?? shadowConfig.offset ?? 0;
        const offsetLng = shadowConfig.offsetLng ?? shadowConfig.offset ?? 0;
        const shadowPath = path.map((point) => ({
          lat: point.lat + offsetLat,
          lng: point.lng + offsetLng,
        }));

        if (!shadowPolylineRef.current) {
          shadowPolylineRef.current = new window.google.maps.Polyline({
            map,
            path: shadowPath,
            strokeWeight: 0,
            strokeColor: shadowConfig.color,
            strokeOpacity: 0,
            clickable: false,
            zIndex: 1,
            icons: [
              {
                icon: {
                  ...routeDashSymbol,
                  strokeColor: shadowConfig.color,
                  strokeOpacity: shadowConfig.opacity,
                  strokeWeight: shadowConfig.strokeWeight,
                },
                offset: "0",
                repeat: "14px",
              },
            ],
          });
        } else {
          shadowPolylineRef.current.setPath(shadowPath);
          shadowPolylineRef.current.setOptions({
            strokeWeight: 0,
            strokeOpacity: 0,
            icons: [
              {
                icon: {
                  ...routeDashSymbol,
                  strokeColor: shadowConfig.color,
                  strokeOpacity: shadowConfig.opacity,
                  strokeWeight: shadowConfig.strokeWeight,
                },
                offset: "0",
                repeat: "14px",
              },
            ],
          });
        }
      }

      if (!polylineRef.current) {
        polylineRef.current = new window.google.maps.Polyline({
          map,
          path,
          strokeWeight: 0,
          strokeColor: "#37D5A4",
          strokeOpacity: 0,
          clickable: false,
          zIndex: 2,
          icons: [
            {
              icon: routeDashSymbol,
              offset: "0",
              repeat: "14px",
            },
          ],
        });
      } else {
        polylineRef.current.setPath(path);
        polylineRef.current.setOptions({
          strokeWeight: 0,
          strokeColor: "#37D5A4",
          strokeOpacity: 0,
          icons: [
            {
              icon: routeDashSymbol,
              offset: "0",
              repeat: "14px",
            },
          ],
        });
      }

      const progressEnd =
        Number.isFinite(progressIndex) && progressIndex >= 1
          ? Math.min(progressIndex + 1, path.length)
          : 0;
      const progressPath =
        progressEnd >= 2 ? path.slice(0, progressEnd) : [];

      if (progressPath.length >= 2) {
        if (!progressPolylineRef.current) {
          progressPolylineRef.current = new window.google.maps.Polyline({
            map,
            path: progressPath,
            strokeWeight: 7,
            strokeColor: "#FFFFFF",
            strokeOpacity: 1,
            clickable: false,
            zIndex: 3,
            icons: [],
          });
        } else {
          progressPolylineRef.current.setPath(progressPath);
          progressPolylineRef.current.setOptions({
            strokeWeight: 7,
            strokeColor: "#FFFFFF",
            strokeOpacity: 1,
            icons: [],
          });
        }
      } else if (progressPolylineRef.current) {
        progressPolylineRef.current.setMap(null);
        progressPolylineRef.current = null;
      }
    } else {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      if (progressPolylineRef.current) {
        progressPolylineRef.current.setMap(null);
        progressPolylineRef.current = null;
      }
      if (shadowPolylineRef.current) {
        shadowPolylineRef.current.setMap(null);
        shadowPolylineRef.current = null;
      }
      if (shadowPolylineRefs.current.length > 0) {
        shadowPolylineRefs.current.forEach((shadow) => {
          if (shadow) shadow.setMap(null);
        });
        shadowPolylineRefs.current = [];
      }
    }
  }, [mapRef]); // shadowConfig 제거하여 무한 루프 방지

  const updateMarkerImages = useCallback(() => {
    const markerSrc = `/waypointMarker.png`;

    markersRef.current.forEach((marker, seq) => {
      const wrapper = marker.content;
      if (!wrapper) return;

      const img = wrapper.querySelector("img");
      const numberSpan = wrapper.querySelector("span");

      if (img) img.src = markerSrc;
      if (numberSpan) numberSpan.textContent = marker._seq.toString();
    });
  }, []);

  const removeMarker = useCallback(
    (id) => {
      const idx = markersRef.current.findIndex((m) => m._id === id);
      if (idx < 0) return;

      const marker = markersRef.current[idx];

      if (marker) marker.setMap(null);
      markersRef.current.splice(idx, 1);

      setCoordinates((prev) => prev.filter((c) => c.id !== id));

      updateMarkerImages();
      updatePolyline();
    },
    [updateMarkerImages, updatePolyline]
  );

  const addMarker = useCallback(
    (
      latlng,
      alt = 0,
      enableAdd = true,
      customId,
      seq = null,
      showLabel = true,
      showIcon = true,
      deferUpdate = false
    ) => {
      if (!mapRef.current) {
        console.error("🗺️ 지도가 초기화되지 않았습니다.");
        return;
      }

      if (
        !latlng ||
        !Number.isFinite(latlng.lat) ||
        !Number.isFinite(latlng.lng)
      ) {
        console.error("Invalid marker position:", latlng);
        return;
      }

      if (!window.google?.maps?.marker?.AdvancedMarkerElement) {
        console.error(
          "🗺️ AdvancedMarkerElement가 로드되지 않았습니다. 지도 초기화를 기다려주세요."
        );
        return;
      }

      const markerSrc = `/waypointMarker.png`;

      // Create wrapper
      const wrapper = document.createElement("div");
      wrapper.style.position = "relative";
      wrapper.style.display = "inline-block";
      if (!showIcon && !showLabel) {
        wrapper.style.width = "0";
        wrapper.style.height = "0";
        wrapper.style.overflow = "hidden";
      }

      let img = null;
      if (showIcon) {
        img = document.createElement("img");
        img.style.width = "70px";
        img.style.cursor = "pointer";
        img.style.pointerEvents = "auto";
        img.setAttribute("draggable", "true");
        img.src = markerSrc;
      }

      const labelSeq = seq ?? markersRef.current.length + 1;
      if (img) wrapper.appendChild(img);

      if (showLabel) {
        const numberSpan = document.createElement("span");
        numberSpan.textContent = String(labelSeq);
        numberSpan.style.position = "absolute";
        numberSpan.style.top = "45%";
        numberSpan.style.left = "50%";
        numberSpan.style.transform = "translate(-50%, -50%)";
        numberSpan.style.color = "white";
        numberSpan.style.fontWeight = "bold";
        numberSpan.style.fontSize = "35px";
        wrapper.appendChild(numberSpan);
      }

      if (img) {
        wrapper.addEventListener("mouseenter", () => {
          img.style.opacity = "0.9";
        });

        wrapper.addEventListener("mouseleave", () => {
          img.style.opacity = "1";
        });
      }

      wrapper.oncontextmenu = (e) => {
        e.preventDefault();
        onMarkerRightClickRef.current?.(marker._id);
      };

      wrapper.onclick = (e) => {
        e.stopPropagation();
        onMarkerClickRef.current?.(marker._id);
      };

      const id = customId;

      if (!window.google.maps.marker?.AdvancedMarkerElement) {
        console.error(
          "AdvancedMarkerElement is not available. Make sure the marker library is loaded."
        );
        return;
      }

      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        map: showIcon || showLabel ? mapRef.current : null,
        position: latlng,
        content: wrapper,
        gmpDraggable: true,
      });

      marker._id = id;
      marker._seq = labelSeq;

      markersRef.current.push(marker);

      if (enableAdd) {
        setCoordinates((prev) => [
          ...prev,
          {
            id,
            lat: +latlng.lat.toFixed(6),
            lng: +latlng.lng.toFixed(6),
            alt: +alt,
            seq,
          },
        ]);
      }

      window.google.maps.event.addListener(marker, "dragstart", () => {
        marker._prev = { ...marker.position };

        if (polylineRef.current) {
          polylineRef.current.setMap(null);
          polylineRef.current = null;
        }
        if (progressPolylineRef.current) {
          progressPolylineRef.current.setMap(null);
          progressPolylineRef.current = null;
        }
        if (shadowPolylineRef.current) {
          shadowPolylineRef.current.setMap(null);
          shadowPolylineRef.current = null;
        }
        if (shadowPolylineRefs.current.length > 0) {
          shadowPolylineRefs.current.forEach((shadow) => {
            if (shadow) shadow.setMap(null);
          });
          shadowPolylineRefs.current = [];
        }
      });

      window.google.maps.event.addListener(marker, "drag", (e) => {
        const dragLat = +e.latLng.lat().toFixed(6);
        const dragLng = +e.latLng.lng().toFixed(6);

        marker.position = {
          lat: dragLat,
          lng: dragLng,
        };
        updatePolyline();
      });

      window.google.maps.event.addListener(marker, "dragend", (e) => {
        const newLat = +e.latLng.lat().toFixed(6);
        const newLng = +e.latLng.lng().toFixed(6);

        marker.position = {
          lat: newLat,
          lng: newLng,
        };

        setCoordinates((prev) => {
          const updated = prev.map((c) =>
            c.id === id ? { ...c, lat: newLat, lng: newLng } : c
          );

          return updated;
        });

        onMarkerDragEndRef.current?.({
          id,
          lat: newLat,
          lng: newLng,
          alt: 0,
        });

        updatePolyline();
      });

      if (!deferUpdate) updatePolyline();
    },
    [mapRef, removeMarker, updatePolyline]
  );

  const setMarkerPosition = useCallback(
    (id, lat, lng) => {
      const marker = markersRef.current.find((m) => m._id === id);
      if (!marker) return;

      marker.position = { lat: +lat, lng: +lng };

      setCoordinates((prev) =>
        prev.map((c) => (c.id === id ? { ...c, lat: +lat, lng: +lng } : c))
      );

      updatePolyline();
    },
    [setCoordinates, updatePolyline, markersRef]
  );

  const revertMarkerPosition = useCallback(
    (id) => {
      const marker = markersRef.current.find((m) => m._id === id);
      if (!marker || !marker._prev) return;

      const { lat, lng } = marker._prev;
      setMarkerPosition(id, lat, lng);
    },
    [markersRef, setMarkerPosition]
  );

  const moveItem = useCallback(
    (from, to) => {
      setCoordinates((prev) => {
        const arr = [...prev];
        const [item] = arr.splice(from, 1);
        arr.splice(to, 0, item);
        return arr;
      });

      const mArr = [...markersRef.current];
      const [m] = mArr.splice(from, 1);
      mArr.splice(to, 0, m);
      markersRef.current = mArr;

      updateMarkerImages();
      updatePolyline();
    },
    [updateMarkerImages, updatePolyline]
  );

  const calculateTotalDistance = useCallback(
    (coords) => {
      let total = 0;
      for (let i = 0; i < coords.length - 1; i++) {
        total += calculate3DDistance(coords[i], coords[i + 1]);
      }
      return total;
    },
    [calculate3DDistance]
  );

  const toggleShadowEffect = useCallback(
    (enable = true) => {
      shadowConfig.enableMultipleShadows = enable;
      setTimeout(() => {
        updatePolyline();
      }, 0);
    },
    [updatePolyline]
  );

  const updateShadowConfig = useCallback(
    (newConfig) => {
      Object.assign(shadowConfig, newConfig);
      setTimeout(() => {
        updatePolyline();
      }, 0);
    },
    [updatePolyline]
  );

  const highlightSelectedMarker = useCallback((selectedId) => {
    const selectedIndex = markersRef.current.findIndex(
      (m) => m._id === selectedId
    );

    markersRef.current.forEach((marker, idx) => {
      const wrapper = marker.content;
      if (!wrapper) return;
      const img = wrapper.querySelector("img");

      if (img) {
        if (idx === selectedIndex) {
          img.style.filter = "drop-shadow(0 0 10px #ffffff) brightness(1.2)";
          img.style.transform = "scale(1.1)";
          img.style.transition = "all 0.3s ease";
        } else {
          img.style.filter = "none";
          img.style.transform = "scale(1)";
          img.style.transition = "all 0.3s ease";
        }
      }
    });
  }, []);

  const moveMarkerUp = useCallback(
    (idx) => {
      if (idx > 0) moveItem(idx, idx - 1);
    },
    [moveItem]
  );

  const moveMarkerDown = useCallback(
    (idx) => {
      if (idx < coordinates.length - 1) moveItem(idx, idx + 1);
    },
    [moveItem, coordinates.length]
  );

  const resetMap = useCallback(() => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (progressPolylineRef.current) {
      progressPolylineRef.current.setMap(null);
      progressPolylineRef.current = null;
    }
    if (shadowPolylineRef.current) {
      shadowPolylineRef.current.setMap(null);
      shadowPolylineRef.current = null;
    }
    if (shadowPolylineRefs.current.length > 0) {
      shadowPolylineRefs.current.forEach((shadow) => {
        if (shadow) shadow.setMap(null);
      });
      shadowPolylineRefs.current = [];
    }
    distanceLabelsRef.current.forEach((lbl) => lbl.setMap(null));
    distanceLabelsRef.current = [];
    setCoordinates([]);
  }, []);

  const clearMarkers = useCallback((preservePolyline = false) => {
    markersRef.current.forEach((m) => m.setMap(null));
    markersRef.current = [];
    distanceLabelsRef.current.forEach((lbl) => lbl.setMap(null));
    distanceLabelsRef.current = [];
    setCoordinates([]);

    if (!preservePolyline) {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
      if (progressPolylineRef.current) {
        progressPolylineRef.current.setMap(null);
        progressPolylineRef.current = null;
      }
      if (shadowPolylineRef.current) {
        shadowPolylineRef.current.setMap(null);
        shadowPolylineRef.current = null;
      }
      if (shadowPolylineRefs.current.length > 0) {
        shadowPolylineRefs.current.forEach((shadow) => {
          if (shadow) shadow.setMap(null);
        });
        shadowPolylineRefs.current = [];
      }
    }
  }, []);

  const setProgressIndex = useCallback(
    (index) => {
      progressIndexRef.current =
        Number.isFinite(index) && index >= 0 ? index : -1;
      updatePolyline();
    },
    [updatePolyline]
  );

  return {
    markersRef,
    addMarker,
    removeMarker,
    setMarkerPosition,
    resetMap,
    clearMarkers,
    updatePolyline,
    setProgressIndex,
    revertMarkerPosition,
    toggleShadowEffect,
    updateShadowConfig,
  };
}

export default useMarkers;
